import { useId, useRef, type ReactNode } from 'react';
import { actions } from '@/lib/actions';
import { useProfileTerms } from '@/lib/hooks';
import { toast, toastError } from '@/lib/toast';
import { Icon } from '@/ui/Icon';
import { Popover, usePopover } from '@/ui/Popover';
import { CapabilityChips, CapabilityItems, MarketItems, SIDE_LABEL, type CapabilitySide } from '@/ui/ProfileTerms';
import { gapsYouFill, hasTerm, PROFILE_LIMITS, sameTerm, termList } from '@shared/profiles';
import type { CompanySheet } from '@shared/record';
import { AiBadge, PickRow } from './Details';
import { EditRow, useCommit, useFlash, useRecordPatch } from './edit';

// The Profile box on a company: what they do, their market and who they sell to, what they are strong at, the
// capabilities they lack, and the team's angle. Gaps the team offers stand out, and the box counts them.

function MarketRow({ sheet, readOnly, badge }: { sheet: CompanySheet; readOnly: boolean; badge: ReactNode }) {
  const commit = useCommit(sheet);
  const pop = usePopover('dialog');
  const [flashing, flash] = useFlash();
  const pick = (market: string | null) => {
    pop.close();
    if (market === sheet.market) return;
    commit('Market', { body: { market }, show: { market } }, { body: { market: sheet.market }, show: { market: sheet.market } })
      .then(flash)
      .catch((err) => toastError(err));
  };
  return (
    <div data-field="market">
      <PickRow label="Market" pop={pop} readOnly={readOnly} flashing={flashing} extra={badge}>
        {sheet.market ?? <span className="fv-empty">{readOnly ? 'None' : 'Add a market, such as AECO'}</span>}
      </PickRow>
      <Popover state={pop} width={260} align="left" role="dialog" label="Choose a market">
        <MarketItems current={sheet.market} onPick={pick} />
      </Popover>
    </div>
  );
}

function CapabilityRow({ sheet, side, readOnly, offerings, badge }: { sheet: CompanySheet; side: CapabilitySide; readOnly: boolean; offerings: string[]; badge: ReactNode }) {
  const patch = useRecordPatch(sheet);
  const pop = usePopover('dialog');
  const [flashing, flash] = useFlash();
  const labelId = useId();
  const label = SIDE_LABEL[side];
  const other: CapabilitySide = side === 'strengths' ? 'lacks' : 'strengths';
  const list = sheet[side];

  const toggle = async (name: string) => {
    const before = { strengths: sheet.strengths, lacks: sheet.lacks };
    const has = hasTerm(list, name);
    const moved = !has && hasTerm(before[other], name);
    const nextSide = has ? list.filter((n) => !sameTerm(n, name)) : [...list, name];
    if (nextSide.length > PROFILE_LIMITS.capabilities) {
      toast(`A company lists up to ${PROFILE_LIMITS.capabilities} ${label.toLowerCase()}.`, { error: true });
      return;
    }
    const next = { ...before, [side]: nextSide, [other]: moved ? before[other].filter((n) => !sameTerm(n, name)) : before[other] };
    try {
      await patch({ [side]: nextSide }, next);
      flash();
      const where = label.toLowerCase();
      toast(has ? `“${name}” removed from ${where}` : moved ? `“${name}” moved to ${where}` : `“${name}” added to ${where}`, {
        icon: 'layers',
        undo: () => {
          patch(before, before).catch((err) => toastError(err));
        },
      });
    } catch (err) {
      toastError(err);
    }
  };

  const addLabel = side === 'strengths' ? 'Add a strength' : 'Add a gap';
  return (
    <div className={['frow prof-caps', flashing ? 'flash' : ''].filter(Boolean).join(' ')} data-field={side} role="group" aria-labelledby={labelId}>
      <span className="fk" id={labelId}>
        {label}
      </span>
      <span className="fv">
        <span className="tags">
          <CapabilityChips names={list} side={side} offerings={offerings} onRemove={readOnly ? undefined : (n) => void toggle(n)} />
          {readOnly ? (
            list.length ? null : <span className="fv-empty">None</span>
          ) : (
            <button type="button" className={list.length ? 'cap cap-new' : 'cap cap-new wide'} aria-label={addLabel} data-tip={list.length ? addLabel : undefined} {...pop.anchorProps}>
              <Icon name="plus" />
              {list.length ? null : addLabel}
            </button>
          )}
          {badge}
        </span>
        {side === 'lacks' && !readOnly && list.length > 0 && offerings.length === 0 ? (
          <button type="button" className="link prof-offer-link" onClick={() => actions.profileTerms('offerings')}>
            Set what you offer
          </button>
        ) : null}
      </span>
      <Popover state={pop} width={270} align="left" role="dialog" label={`${label} of ${sheet.name}`}>
        <CapabilityItems side={side} strengths={sheet.strengths} lacks={sheet.lacks} onToggle={(n) => void toggle(n)} />
      </Popover>
    </div>
  );
}

export function ProfileBox({ sheet, readOnly }: { sheet: CompanySheet; readOnly: boolean }) {
  const terms = useProfileTerms();
  const commit = useCommit(sheet);
  const box = useRef<HTMLDivElement>(null);
  const offerings = terms.data?.offerings ?? [];
  const fits = gapsYouFill(sheet.lacks, offerings);
  const text = (field: 'summary' | 'clients' | 'angle', label: string) => (next: string) =>
    commit(label, { body: { [field]: next || null }, show: { [field]: next || null } }, { body: { [field]: sheet[field] }, show: { [field]: sheet[field] } });
  // "Edit value" from a badge's menu opens the row for typing.
  const edit = (field: string) => () => box.current?.querySelector<HTMLElement>(`[data-field="${field}"] .frow.ed`)?.click();
  const badge = (field: string, onEdit?: () => void) => (readOnly ? null : <AiBadge sheet={sheet} field={field} onEdit={onEdit} />);
  return (
    <section className="tile prof" aria-labelledby={`prof-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`prof-${sheet.id}`}>Profile</h3>
        {fits.length ? (
          <span className="chip chip-accent prof-fit" data-tip={`You offer ${termList(fits)}`}>
            <Icon name="target" />
            {fits.length === 1 ? 'You fill 1 gap' : `You fill ${fits.length} gaps`}
          </span>
        ) : null}
        {readOnly ? null : (
          <button type="button" className="icon-btn xs" aria-label="What you offer, capabilities and markets" data-tip="What you offer" onClick={() => actions.profileTerms('offerings')}>
            <Icon name="sliders" />
          </button>
        )}
      </div>
      <div className="fields" ref={box}>
        <div data-field="summary">
          <EditRow
            label="What they do"
            field="summary"
            value={sheet.summary ?? ''}
            empty="Add what they do, in a line"
            readOnly={readOnly}
            save={text('summary', 'What they do')}
            extra={badge('summary', edit('summary'))}
          />
        </div>
        <MarketRow sheet={sheet} readOnly={readOnly} badge={badge('market')} />
        <div data-field="clients">
          <EditRow label="Sells to" field="clients" value={sheet.clients ?? ''} empty="Add who they sell to" readOnly={readOnly} save={text('clients', 'Sells to')} extra={badge('clients', edit('clients'))} />
        </div>
        <CapabilityRow sheet={sheet} side="strengths" readOnly={readOnly} offerings={offerings} badge={badge('strengths')} />
        <CapabilityRow sheet={sheet} side="lacks" readOnly={readOnly} offerings={offerings} badge={badge('lacks')} />
        <div data-field="angle">
          <EditRow
            label="Our angle"
            field="angle"
            kind="long"
            value={sheet.angle ?? ''}
            display={<span className="pre">{sheet.angle}</span>}
            empty="Add how to approach them"
            readOnly={readOnly}
            save={text('angle', 'Our angle')}
          />
        </div>
      </div>
    </section>
  );
}
